/**
 * `useUploadQueue` — encapsulates the chip queue + warning banners + submit
 * lifecycle for the Add Artifacts → Upload sub-flow.
 *
 * Owned by `AddArtifactsModal` (lifted state — same pattern as the picker
 * step) so the modal's footer Add button reads queue size + in-flight state
 * directly without UploadView having to emit a controller-handle through a
 * `useEffect`. UploadView becomes a pure render component that receives the
 * handlers + state via props.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useUploadArtifacts, type ChipStatus } from '@/features/manage-project-artifacts';
import type { UploadChip } from './UploadView.types';

interface UploadWarning {
  id: string;
  message: string;
}

/**
 * `crypto.randomUUID` is unavailable in non-secure-context browsers (HTTP
 * localhost is fine; HTTPS prod is fine; older insecure deployments fall
 * back). A Math.random-based v4-shaped ID is good enough for ephemeral chip /
 * warning identifiers — they never leave the page.
 */
function generateClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface UseUploadQueueOptions {
  projectId: string;
  onAllSucceeded: () => void;
}

export interface UploadQueueState {
  chips: UploadChip[];
  warnings: UploadWarning[];
  isAnyUploading: boolean;
  /** True iff at least one chip is in the `error` status — drives the footer "Retry (N)" label. */
  hasErrors: boolean;
  submittableChips: UploadChip[];
  isPending: boolean;
  handleDrop: (accepted: File[], rejections: FileRejection[]) => void;
  handleRemove: (id: string) => void;
  dismissWarning: (id: string) => void;
  submit: () => void;
  /** Drop all chips + warnings — modal calls this when leaving the upload step via Back. */
  reset: () => void;
}

export function useUploadQueue(options: UseUploadQueueOptions): UploadQueueState {
  const { t } = useTranslation('projects');

  const { projectId, onAllSucceeded } = options;
  const [chips, setChips] = useState<UploadChip[]>([]);
  const [warnings, setWarnings] = useState<UploadWarning[]>([]);

  // Stable identity for the per-chip callback so the mutation doesn't capture
  // a stale closure across renders. Reviewer F4: in-flight `runOne` calls
  // dispatched on render N can fire `onPerFile` long after render N+1 has
  // produced a fresh callback identity; reading through `.current` always
  // hits the latest.
  const onPerFileRef = useRef<(id: string, status: ChipStatus, errorMessage?: string) => void>(
    () => {},
  );
  onPerFileRef.current = useCallback((id: string, status: ChipStatus, errorMessage?: string) => {
    setChips((prev) =>
      prev.map((chip) => (chip.id === id ? { ...chip, status, errorMessage } : chip)),
    );
  }, []);

  const { mutateAsync, isPending } = useUploadArtifacts({
    projectId,
    onPerFile: (id, status, errorMessage) => {
      onPerFileRef.current(id, status, errorMessage);
    },
  });

  const dismissWarning = useCallback((id: string) => {
    setWarnings((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const isAnyUploading = chips.some((chip) => chip.status === 'uploading');
  const hasErrors = chips.some((chip) => chip.status === 'error');

  const submittableChips = useMemo(
    () => chips.filter((chip) => chip.status === 'pending' || chip.status === 'error'),
    [chips],
  );

  // Mirror `chips` in a ref so `handleDrop` can read the latest snapshot at
  // call time without needing to sit inside a setter (which forced the old
  // impure `setWarnings` call from within `setChips`, doubling under strict
  // mode). The ref stays in sync via the `chipsRef.current = chips` line below.
  const chipsRef = useRef<UploadChip[]>([]);
  chipsRef.current = chips;

  const handleDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      // Pure computation FIRST: both nextChips and
      // nextWarnings are computed synchronously from the current snapshot, then
      // both setters run back-to-back. React batches them, and neither runs
      // inside the other's updater — so strict-mode's double-invoke of a state
      // updater cannot double-fire `setWarnings`.
      const prevChips = chipsRef.current;
      const isDuplicate = (file: File) =>
        prevChips.some(
          (existing) => existing.file.name === file.name && existing.file.size === file.size,
        );
      const duplicates = accepted.filter(isDuplicate);
      const fresh = accepted.filter((file) => !isDuplicate(file));
      const oversized = rejections
        .filter((r) => r.errors.some((e) => e.code === 'file-too-large'))
        .map((r) => r.file);
      const wrongType = rejections
        .filter((r) => r.errors.some((e) => e.code === 'file-invalid-type'))
        .map((r) => r.file);

      const nextWarnings: UploadWarning[] = [];
      if (duplicates.length > 0) {
        nextWarnings.push({
          id: generateClientId(),
          message: t('projectDetail.useUploadQueue.filesAlreadyAdded', {
            count: duplicates.length,
          }),
        });
      }
      if (oversized.length > 0) {
        nextWarnings.push({
          id: generateClientId(),
          message: t('projectDetail.useUploadQueue.filesExceededThe10MbSize', {
            count: oversized.length,
          }),
        });
      }
      if (wrongType.length > 0) {
        nextWarnings.push({
          id: generateClientId(),
          message: t('projectDetail.useUploadQueue.unsupportedFilesSkipped', {
            count: wrongType.length,
          }),
        });
      }

      setWarnings(nextWarnings);

      if (fresh.length === 0) return;
      const freshChips: UploadChip[] = fresh.map((file) => ({
        id: generateClientId(),
        file,
        status: 'pending',
      }));
      setChips([...prevChips, ...freshChips]);
    },
    [t],
  );

  const handleRemove = useCallback((id: string) => {
    setChips((prev) => prev.filter((chip) => chip.id !== id));
  }, []);

  const reset = useCallback(() => {
    setChips([]);
    setWarnings([]);
  }, []);

  // Hold the latest onAllSucceeded in a ref so the submit closure stays
  // stable across renders without re-binding the callback identity.
  const onAllSucceededRef = useRef(onAllSucceeded);
  onAllSucceededRef.current = onAllSucceeded;

  const submit = useCallback(() => {
    if (submittableChips.length === 0) return;
    void (async () => {
      const result = await mutateAsync(submittableChips);
      if (result.errored === 0) {
        onAllSucceededRef.current();
        return;
      }
      // Mixed/error: drop the `done` chips so the next Submit re-attempts the
      // failed ones only. Error chips keep their error message + destructive
      // styling so the user sees what to retry.
      setChips((prev) => prev.filter((chip) => chip.status !== 'done'));
    })();
  }, [mutateAsync, submittableChips]);

  return {
    chips,
    warnings,
    isAnyUploading,
    hasErrors,
    submittableChips,
    isPending,
    handleDrop,
    handleRemove,
    dismissWarning,
    submit,
    reset,
  };
}
